/**
 * The checklist the model answered from the picture alone.
 *
 * Shown in full, including the questions that came back fine. A list of only
 * the bad answers hides how much was actually looked at, and an operator who
 * cannot see the clean answers has no way to tell a thorough run from a run
 * where the picture arrived blank.
 *
 * WHICH ANSWER IS "BAD" IS NOT DECIDED HERE. The server owns that table
 * (`passes/legend.ts`) and it owns the severity too, because a model asked how
 * serious something is says "critical" about everything. So this panel marks a
 * row as a defect only when a finding was actually produced for it - the same
 * rule, applied once, on the side that knows it.
 *
 * No numbers anywhere in this panel, deliberately. Every measurement in this
 * tool comes from the layout probe above; a picture is not a ruler.
 */
import { Check, Eye, HelpCircle, X } from "lucide-react";
import type { BrainFinding, BrainVision, BrainViewport } from "../../../../lib/siteBrain";
import { SEVERITY_COLOUR } from "../../site-scanner/severity";
import type { T } from "../../site-scanner/types";

const ANSWER_ICON = { yes: Check, no: X, unclear: HelpCircle } as const;

export function BrainVisionPanel({
  vision,
  findings,
  t,
}: {
  vision: BrainVision | null;
  findings: BrainFinding[];
  t: T;
}) {
  if (!vision) return null;

  // A vision finding carries the viewport it was answered from, which is what
  // lets one question be fine on the desktop frame and a defect on the phone.
  const defects = new Set(
    findings
      .filter((f) => f.source === "vision")
      .map((f) => `${f.kind}:${String(f.vars?.viewport ?? "")}`),
  );

  if (!vision.observations.length) {
    return <p className="text-sm text-muted-foreground">{t("brain.vision.none")}</p>;
  }

  return (
    <section className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <Eye className="size-4 text-primary" />
        {t("brain.vision.title")}
      </h4>

      <ul className="space-y-1">
        {vision.observations.map((o, i) => (
          <Row
            key={`${o.kind}:${o.viewport}:${i}`}
            kind={o.kind}
            answer={o.answer}
            note={o.note}
            viewport={o.viewport}
            defect={defects.has(`${o.kind}:${o.viewport}`)}
            t={t}
          />
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">{t("brain.vision.model", { model: vision.model })}</p>
    </section>
  );
}

function Row({
  kind,
  answer,
  note,
  viewport,
  defect,
  t,
}: {
  kind: string;
  answer: "yes" | "no" | "unclear";
  note: string;
  viewport: BrainViewport;
  defect: boolean;
  t: T;
}) {
  const Icon = ANSWER_ICON[answer];
  // Three states, three colours: a defect, an answer that told us nothing, and
  // a clean one. Grey for "unclear" matters - it is not a pass.
  const colour = defect ? SEVERITY_COLOUR.warning : answer === "unclear" ? SEVERITY_COLOUR.info : SEVERITY_COLOUR.good;

  return (
    <li className="flex items-start gap-2.5 rounded-xl bg-[var(--crm-sunk)] px-3 py-2">
      <Icon className="mt-0.5 size-3.5 shrink-0" style={{ color: colour }} />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">{t(`brain.check.${kind}`)}</p>
        {note ? <p className="text-xs leading-snug text-muted-foreground">{note}</p> : null}
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">{t(`brain.look.viewport.${viewport}`)}</span>
    </li>
  );
}
